import React, { Component } from 'react';
import { Box, Stack, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Permissions } from 'Permission';
import { PermissionGate } from 'PermissionContext';
import BackendService from '../../services/admin/BackendService';
import Contact from '../../models/Contact';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';
import MessageToast from '../MessageToast';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';
import { StyledHeading } from '../styles/styles';
import { InvestmentEntitiesSection } from './InvestmentEntitiesSection';
import { ContactInvitationSection } from './ContactAdmin/ContactInvitationSection';
import ContactFormManager from './ContactAdmin/ContactFormManager';
import ContactFundOpportunityKanban from './ContactAdmin/ContactFundOpportunityKanban';
import ContactRecentlyViewedSection from './ContactRecentlyViewedSection';
import ContactSearchBar from './ContactSearchBar';

interface WrapperProps {
  tone?: keyof typeof Palette;
  id?: string;
}

interface Props {
  contactId?: string;
  tone?: keyof typeof Palette;
  navigate: (path: string) => void;
  trackView: (modelType: string, modelId: string) => void;
}

interface State {
  contact: Contact | null;
  loading: boolean;
  error?: string;
  message?: { type: 'success' | 'error' | 'warning'; text: string };
  investmentEntities: InvestmentEntity[];
  investmentEntitiesLoading: boolean;
  investmentEntitiesLoaded: boolean;
  entityFilter: string;
  generatingToken: boolean;
  linkCopied: boolean;
}

const ContactAdmin = (props: WrapperProps) => {
  const navigate = useNavigate();
  const { trackView } = useRecentlyViewed();

  return (
    <ContactAdminContainer
      tone={props.tone}
      contactId={props.id}
      navigate={navigate}
      trackView={trackView}
    />
  );
};

class ContactAdminContainer extends Component<Props, State> {
  static defaultProps = {
    tone: 'default',
  };

  private copyTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      contact: null,
      loading: true,
      investmentEntities: [],
      investmentEntitiesLoading: false,
      investmentEntitiesLoaded: false,
      entityFilter: '',
      generatingToken: false,
      linkCopied: false,
    };
  }

  componentDidMount() {
    this.loadContact();
  }

  componentWillUnmount() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.contactId !== prevProps.contactId) {
      this.loadContact();
    }

    if (
      this.state.contact &&
      prevState.contact?.id !== this.state.contact?.id &&
      !this.state.investmentEntitiesLoaded
    ) {
      this.loadInvestmentEntities(this.state.contact.id);
    }
  }

  private loadContact = async () => {
    const { contactId } = this.props;

    if (!contactId) {
      // "Add Contact" mode — no ID means a new contact form
      this.setState({ loading: false });
      return;
    }

    this.setState({ loading: true, error: undefined });

    try {
      const service = new BackendService();
      const contact = await service.getContact(contactId);

      if (contact) {
        const prevId = this.state.contact?.id;
        if (prevId !== contact.id) {
          this.setState({
            contact,
            loading: false,
            investmentEntities: [],
            investmentEntitiesLoaded: false,
            investmentEntitiesLoading: false,
            entityFilter: '',
          });
        } else {
          this.setState({ contact, loading: false });
        }
        this.props.trackView('CONTACT', contact.id);
      } else {
        this.setState({
          loading: false,
          error: 'Contact not found',
        });
      }
    } catch (error: unknown) {
      console.error('Failed to load contact:', error);
      this.setState({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load contact',
      });
    }
  };

  private loadInvestmentEntities = (contactId: string) => {
    this.setState({ investmentEntitiesLoading: true });
    new BackendService()
      .getInvestmentEntitiesForUser(contactId)
      .then((entities) => {
        this.setState({
          investmentEntities: entities || [],
          investmentEntitiesLoading: false,
          investmentEntitiesLoaded: true,
        });
      })
      .catch((error) => {
        console.error('Error loading investment entities:', error);
        this.setState({
          investmentEntitiesLoading: false,
          investmentEntitiesLoaded: true,
        });
      });
  };

  private handleContactUpdate = (updatedContact: Contact) => {
    this.setState({ contact: updatedContact });
  };

  private handleDataRefresh = () => {
    this.loadContact();
  };

  private showSuccess = (message: string) => {
    this.setState({
      message: { type: 'success', text: message },
    });
  };

  private showError = (message: string) => {
    this.setState({
      message: { type: 'error', text: message },
    });
  };

  private showWarning = (message: string) => {
    this.setState({
      message: { type: 'warning', text: message },
    });
  };

  private clearMessage = () => {
    this.setState({ message: undefined });
  };

  private handleGenerateToken = () => {
    const { contact } = this.state;
    if (!contact) return;
    this.setState({ generatingToken: true });
    new BackendService()
      .generateInvitationToken(contact.id)
      .then(() => {
        this.setState({ generatingToken: false });
        this.handleDataRefresh();
      })
      .catch((error: any) => {
        this.setState({ generatingToken: false });
        this.showError(`Failed to generate invitation token: ${error.message || 'Unknown error'}`);
      });
  };

  private handleRegenerateToken = () => {
    const { contact } = this.state;
    if (!contact) return;
    new BackendService()
      .regenerateInvitationToken(contact.id)
      .then(() => {
        this.handleDataRefresh();
      })
      .catch((error: any) => {
        this.showError(
          `Failed to regenerate invitation token: ${error.message || 'Unknown error'}`,
        );
      });
  };

  private handleCopyInvitationLink = () => {
    const { contact, linkCopied } = this.state;
    if (!contact?.invitationLink || linkCopied) return;

    navigator.clipboard.writeText(contact.invitationLink);
    this.setState({ linkCopied: true });
    this.copyTimeout = setTimeout(() => this.setState({ linkCopied: false }), 3000);
  };

  render() {
    const { loading, error, message, contact } = this.state;
    const { tone = 'default' } = this.props;

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (this.props.contactId && !contact) {
      if (error) {
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <StyledButton variant="contained" onClick={this.loadContact}>
              Try Again
            </StyledButton>
          </Box>
        );
      }
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Contact not found</Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ py: 3 }}>
          <Stack spacing={3}>
            <StyledSection tone={tone} noTopPadding>
              <StyledHeading tone={tone} fullTopSpacing>
                {contact ? `Contact: ${contact.firstName} ${contact.lastName}` : 'Contacts'}
              </StyledHeading>
              {!contact && (
                <StyledHeading headerVariant="subheading" tone={tone}>
                  Add New Contact
                </StyledHeading>
              )}
              <ContactFormManager
                contact={contact}
                onContactUpdate={this.handleContactUpdate}
                onContactCreated={(newId) => this.props.navigate(`/contacts/${newId}`)}
                onError={this.showError}
                onSuccess={this.showSuccess}
              />
            </StyledSection>

            {!contact && (
              <StyledSection tone="tone1" noTopPadding>
                <ContactSearchBar tone={tone} />
              </StyledSection>
            )}

            {!contact && (
              <StyledSection tone="tone1" noTopPadding>
                <ContactRecentlyViewedSection />
              </StyledSection>
            )}

            {contact && (
              <PermissionGate permissionKey={Permissions.ContactCreation}>
                <StyledSection tone="tone1">
                  <ContactInvitationSection
                    contact={contact}
                    generatingToken={this.state.generatingToken}
                    linkCopied={this.state.linkCopied}
                    onGenerateToken={this.handleGenerateToken}
                    onRegenerateToken={this.handleRegenerateToken}
                    onNavigateToEmail={(id) =>
                      this.props.navigate(`/contacts/${id}/generateInvitationEmail`)
                    }
                    onCopyLink={this.handleCopyInvitationLink}
                  />
                </StyledSection>
              </PermissionGate>
            )}

            {contact && (
              <StyledSection tone="tone1">
                <InvestmentEntitiesSection
                  entities={this.state.investmentEntities}
                  loading={this.state.investmentEntitiesLoading}
                  loaded={this.state.investmentEntitiesLoaded}
                  filter={this.state.entityFilter}
                  tone="tone1"
                  onFilterChange={(filter) => this.setState({ entityFilter: filter })}
                  onLoadEntities={() => this.loadInvestmentEntities(contact.id)}
                  currentContactId={contact.id}
                />
              </StyledSection>
            )}

            {contact && (
              <StyledSection tone="tone1" sx={{ position: 'relative' }}>
                <ContactFundOpportunityKanban
                  contactId={contact.id}
                  contact={contact}
                  onError={this.showError}
                  onSuccess={this.showSuccess}
                  onWarning={this.showWarning}
                  onReloadContact={this.handleDataRefresh}
                  tone={tone}
                />
              </StyledSection>
            )}
          </Stack>
        </Box>

        {message && (
          <MessageToast
            message={message.text}
            type={message.type}
            onClose={this.clearMessage}
            open={!!message}
            autoHideDuration={message.type === 'warning' ? 8000 : 10000}
          />
        )}
      </>
    );
  }
}

export default ContactAdmin;
